import { Link } from 'react-router';
import { Calendar, Clock, Share2 } from 'lucide-react';

type Props = {
  title: string;
  description: string;
  date: string;
  author: string;
  tags: ReadonlyArray<string>;
  readMinutes: number;
  cover?: string;
};

function formatFullDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return d;
  }
}

function HeroGradient({ cover }: { cover?: string }) {
  return (
    <div className="relative h-64 overflow-hidden border-2 border-charcoal-800 bg-[conic-gradient(from_135deg,#84CC16_0deg,#1A1A2E_90deg,#EA580C_180deg,#84CC16_270deg,#1A1A2E_360deg)] shadow-neobrutal-10 md:h-80">
      {cover ? (
        <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(245,243,239,0.25),transparent_65%)]" />
      )}
      <div className="absolute left-6 top-6 inline-flex items-center gap-1.5 border-2 border-charcoal-800 bg-white px-3 py-1 font-marketing-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-charcoal-800 shadow-neobrutal-2">
        <span className="h-2 w-2 rounded-full border border-charcoal-800 bg-lime-500" />
        Article lecture
      </div>
    </div>
  );
}

export default function BlogPostHeroV2({
  title,
  description,
  date,
  author,
  tags,
  readMinutes,
  cover,
}: Props) {
  const onShare = () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      void navigator.share({ title, url: window.location.href }).catch(() => {});
    } else if (typeof navigator !== 'undefined') {
      void navigator.clipboard?.writeText(window.location.href).catch(() => {});
    }
  };

  return (
    <section className="relative bg-paper pb-12 pt-10 md:pb-16 md:pt-14">
      <div className="mx-auto max-w-5xl px-6 md:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-600 hover:text-charcoal-900"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M19 12H5" />
            <path d="M11 6l-6 6 6 6" />
          </svg>
          Retour au journal
        </Link>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center border-2 border-charcoal-800 bg-white px-2.5 py-1 font-marketing-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-charcoal-800 shadow-neobrutal-2"
            >
              {tag}
            </span>
          ))}
        </div>

        <h1 className="mt-6 font-marketing-display text-[clamp(34px,5.6vw,78px)] font-bold leading-[0.98] tracking-[-0.04em] text-charcoal-800">
          {title}
        </h1>

        <p className="mt-6 max-w-3xl font-marketing-body text-lg leading-[1.55] text-charcoal-600 md:text-xl">
          {description}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t-2 border-dashed border-charcoal-200 pt-5 font-marketing-mono text-[11.5px] font-medium uppercase tracking-[0.12em] text-charcoal-600">
          <span className="inline-flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5" strokeWidth={2.4} />
            {formatFullDate(date)}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" strokeWidth={2.4} />
            {readMinutes} min de lecture
          </span>
          <span className="inline-flex items-center gap-2">
            Par <span className="text-charcoal-900">{author}</span>
          </span>
          <button
            type="button"
            onClick={onShare}
            className="ml-auto inline-flex items-center gap-2 border-2 border-charcoal-800 bg-white px-3 py-1.5 font-marketing-display text-[12px] font-bold text-charcoal-800 shadow-neobrutal-2 transition-transform hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-neobrutal-4 cursor-pointer"
          >
            <Share2 className="h-3.5 w-3.5" strokeWidth={2.4} />
            Partager
          </button>
        </div>

        <div className="mt-10">
          <HeroGradient cover={cover} />
        </div>
      </div>
    </section>
  );
}
